import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';

export default function AddressAutocomplete({ 
  onAddressSelect, 
  placeholder = "Start typing your address...",
  className = "",
  defaultValue = ""
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading, ready

  useEffect(() => {
    let mounted = true;
    let attempts = 0;
    const maxAttempts = 10;

    const initAutocomplete = () => {
      if (!mounted) return;
      
      attempts++;
      
      // Check if Google Maps is loaded
      if (!window.google?.maps?.places) {
        if (attempts < maxAttempts) {
          setTimeout(initAutocomplete, 500);
        } else {
          // Even if Google fails to load, mark as ready so user can type manually
          setStatus('ready');
        }
        return;
      }

      if (!inputRef.current || autocompleteRef.current) {
        setStatus('ready');
        return;
      }

      try {
        // Create autocomplete instance
        const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
          componentRestrictions: { country: ['au', 'nz'] },
          fields: ['address_components', 'formatted_address'],
          types: ['address']
        });

        // Store reference
        autocompleteRef.current = autocomplete;

        // Add place_changed listener
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          
          if (place?.address_components) {
            const addressData = parseAddress(place.address_components);
            addressData.formatted_address = place.formatted_address || '';
            onAddressSelect(addressData);
          }
        });

        setStatus('ready');
      } catch (err) {
        console.error('Google Places init error:', err);
        // Still mark as ready so the input is usable
        setStatus('ready');
      }
    };

    initAutocomplete();

    return () => {
      mounted = false;
    };
  }, [onAddressSelect]);

  const parseAddress = (components) => {
    const result = {
      street_number: '',
      street_name: '',
      street: '',
      city: '',
      state: '',
      postcode: '',
      country: '',
      country_code: ''
    };

    for (const comp of components) {
      const types = comp.types;
      if (types.includes('street_number')) result.street_number = comp.long_name;
      if (types.includes('route')) result.street_name = comp.long_name;
      if (types.includes('locality')) result.city = comp.long_name;
      if (types.includes('sublocality_level_1') && !result.city) result.city = comp.long_name;
      if (types.includes('administrative_area_level_1')) result.state = comp.short_name;
      if (types.includes('postal_code')) result.postcode = comp.long_name;
      if (types.includes('country')) {
        result.country = comp.long_name;
        result.country_code = comp.short_name;
      }
    }

    result.street = [result.street_number, result.street_name].filter(Boolean).join(' ');
    return result;
  };

  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10">
        {status === 'loading' ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <MapPin className="h-4 w-4" />
        )}
      </div>
      <input
        ref={inputRef}
        type="text"
        defaultValue={defaultValue}
        placeholder={placeholder}
        className={`flex h-9 w-full rounded-md border border-input bg-transparent pl-10 pr-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm bg-background border-border ${className}`}
        data-testid="address-autocomplete-input"
        autoComplete="off"
      />
      {status === 'loading' && (
        <p className="text-xs text-muted-foreground mt-1">Loading address search...</p>
      )}
    </div>
  );
}
